import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { createI18nGetter } from "../lib/i18n_helper"
import { showSuccess as showSuccessToast, showInfo as showInfoToast } from "../lib/platform_toast"
import { NativeBridge } from "../lib/native_bridge"

// Production-ready Web Share Controller
// Features:
// - Native share integration for Android/iOS (NativeBridge)
// - Loading state (중복 클릭 방지)
// - iOS Safari compatible manual copy UI
// - Toast integration via ToastController
// - Progressive fallback: Native Share → Web Share → Clipboard → Manual UI
// - I18n support via data-i18n-* attributes
// - Image sharing support (imageUri)
const log = createLogger('Share')

export default class extends Controller {
  static values = {
    title: String,
    text: String,
    url: String,
    imageUri: String
  }

  connect() {
    log.debug('connect', {
      nativeApp: NativeBridge.isNative(),
      webShare: !!navigator.share,
      clipboard: !!navigator.clipboard,
      protocol: window.location.protocol
    })

    this.isSharing = false
    this.isNative = NativeBridge.isNative()
    this.i18n = createI18nGetter(this)
  }

  async share(event) {
    // 중복 클릭 방지
    if (this.isSharing) {
      log.debug('Already sharing, ignoring duplicate click')
      return
    }

    this.isSharing = true
    const button = event.currentTarget

    // Loading state 활성화
    button.disabled = true
    button.classList.add("opacity-50", "cursor-wait")

    try {
      await this._executeShare()
    } finally {
      // 항상 복구 (성공/실패 관계없이)
      this.isSharing = false
      button.disabled = false
      button.classList.remove("opacity-50", "cursor-wait")
    }
  }

  async _executeShare() {
    log.info('Share button clicked', {
      isNative: this.isNative,
      title: this.titleValue,
      text: this.textValue,
      url: this.urlValue,
      imageUri: this.imageUriValue
    })

    // Native App: NativeBridge.Share.share() 사용
    if (this.isNative) {
      log.debug('Using Native Share (NativeBridge)')
      try {
        const shareData = {
          title: this.titleValue,
          text: this.textValue,
          url: this.urlValue
        }

        // 이미지 URI가 있으면 추가
        if (this.hasImageUriValue && this.imageUriValue) {
          shareData.imageUri = this.imageUriValue
          log.debug('Including image in share', { imageUri: this.imageUriValue })
        }

        await NativeBridge.Share.share(shareData)
        log.info('Native share successful')
        this._showSuccessToast()
        return // 성공 시 종료
      } catch (error) {
        if (error.message && error.message.includes('cancel')) {
          log.debug('User cancelled native share')
          return // 사용자 취소 → 조용히 종료
        }
        log.error('Native share failed, falling back to Web Share', error)
        // 에러 발생 시 Web Share로 폴백
      }
    }

    // Web Environment: Web Share API 시도
    const shareData = {
      title: this.titleValue,
      text: this.textValue,
      url: this.urlValue
    }

    if (navigator.share) {
      log.debug('Using Web Share API')
      try {
        await navigator.share(shareData)
        log.info('Web share successful')
        this._showSuccessToast()
        return // 성공 시 종료
      } catch (error) {
        if (error.name === "AbortError") {
          log.debug('User cancelled share')
          return // 사용자 취소 → 조용히 종료
        }
        log.error('Web share failed', error)
        // 실패 시 Clipboard 폴백으로 진행
      }
    } else {
      log.warn('Web Share API not supported')
    }

    // Clipboard API 폴백
    await this._fallbackToCopy()
  }

  async _fallbackToCopy() {
    log.debug('Attempting clipboard copy')

    if (!navigator.clipboard) {
      log.error('Clipboard API not available')
      this._showManualCopyUI()
      return
    }

    try {
      await navigator.clipboard.writeText(this.urlValue)
      log.info('Clipboard copy successful')
      this._showCopyToast()
    } catch (error) {
      log.error('Clipboard copy failed', error)
      // iOS Safari 제약 → Manual UI
      this._showManualCopyUI()
    }
  }

  // iOS Safari 호환: Server-rendered modal via Turbo Frame
  async _showManualCopyUI() {
    log.info('Showing manual copy UI (server-rendered)')

    // Show info toast
    this._showToast(this.i18n('ManualCopyRequired', '請手動複製連結'), "info")

    try {
      // Fetch server-rendered modal
      const url = `/share/manual_copy_modal?url=${encodeURIComponent(this.urlValue)}`
      const response = await fetch(url)

      if (response.ok) {
        const html = await response.text()
        document.body.insertAdjacentHTML('beforeend', html)
        log.info('Manual copy modal rendered (server-side)')
      } else {
        log.error('Failed to load manual copy modal', { status: response.status })
      }
    } catch (error) {
      log.error('Manual copy modal error', error)
    }
  }

  // Toast 통합: I18n support via data-i18n-* attributes
  // Platform-aware: iOS uses native toast, Web/Android uses web toast
  _showSuccessToast() {
    const message = this.i18n('Success', '分享成功')
    log.debug('Showing success toast', { message })
    showSuccessToast(message)
  }

  _showCopyToast() {
    const message = this.i18n('ClipboardSuccess', '連結已複製到剪貼簿')
    log.debug('Showing copy toast', { message })
    showInfoToast(message)
  }

  _showToast(message, variant) {
    log.debug('Showing toast', { message, variant })
    // Use platform_toast for iOS native support
    if (variant === "success") {
      showSuccessToast(message)
    } else {
      showInfoToast(message)
    }
  }
}
